import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnDestroy,
} from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { NotesService } from '../../services/notes.service';
import {
  NoteViewModel,
  TrashedNoteViewModel,
  NoteFilterParams,
} from '../../models/note.model';
import { DecryptPipe } from '../../../auth/_services/encrypt-decrypt.pipe';
import { CookieService } from 'ngx-cookie-service';

type PanelView = 'list' | 'editor' | 'trash';
type FilterTab = 'all' | 'pinned' | 'archived' | 'reminder';

@Component({
  selector: 'app-notes-panel',
  templateUrl: './notes-panel.component.html',
  styleUrls: ['./notes-panel.component.scss'],
})
export class NotesPanelComponent implements OnInit, OnChanges, OnDestroy {  
  @Input() TransactionType: string;
  @Input() TransactionId: string;
  @Input() isOpen: boolean = false;

  @Output() onClose = new EventEmitter<void>();
  @Output() noteCountChange = new EventEmitter<number>();
  @Output() refreshParentList = new EventEmitter<void>();

  currentView: PanelView = 'list';
  activeFilterTab: FilterTab = 'all';

  notes: NoteViewModel[] = [];
  trashedNotes: TrashedNoteViewModel[] = [];
  selectedNote: NoteViewModel | null = null;

  pageNumber: number = 1;
  pageSize: number = 20;
  totalCount: number = 0;
  trashTotalCount: number = 0;
  trashPageNumber: number = 1;

  searchTerm: string = '';
  searchSubject = new Subject<string>();
  filterNoteType: string = '';

  isLoading: boolean = false;
  isTrashLoading: boolean = false;
  showDeleteConfirm: string | null = null;
  showPermDeleteConfirm: string | null = null;   
  unreadMentionCount: number = 0;

  currentUserId: string = '';
  currentUserName: string = '';

  hasDueReminders: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private notesService: NotesService,
    private toastr: ToastrService,
    private decryptpipe: DecryptPipe,
    private cookieService: CookieService
  ) {}

  ngOnInit(): void {
    this.getCurrentUser();
    this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => {
        this.pageNumber = 1;
        this.loadNotes();
      });

    this.notesService.notesRefresh$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.loadNotes());

    this.notesService.unreadMentionCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe((count) => (this.unreadMentionCount = count));

    if (this.TransactionType && this.TransactionId) {   
      this.loadNotes();
      this.notesService.getUnreadMentions().subscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('NotesPanel - ngOnChanges:', changes);
    if (
      (changes.TransactionType || changes.TransactionId) && 
      this.TransactionType &&
      this.TransactionId
    ) {
      this.resetState();
      this.loadNotes();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getCurrentUser(): void {
    if (this.cookieService.get("UserId")) {
      try {
        this.currentUserId = this.decryptpipe.transform(
          JSON.parse(this.cookieService.get("UserId"))
        );
      } catch (e) {
        console.error('Error decrypting user ID:', e);
      }
    }

    if (this.cookieService.get('UserFullName')) {
      try {
        this.currentUserName = this.decryptpipe.transform(
          JSON.parse(this.cookieService.get("UserFullName"))
        );
      } catch (e) {
        console.error('Error decrypting user name:', e);
      }
    }

    this.currentUserId = String(this.currentUserId).replace(/[{}"]/g, '').toLowerCase();
    console.log('Current User ID in NotesPanel:', this.currentUserId);

    console.log('Cookie UserId raw:', this.cookieService.get("UserId"));
  }

  canEditNote(note: NoteViewModel): boolean {
  const noteCreator = String(note.CreatedBy).replace(/[{}"]/g, '').toLowerCase();
  const currentUser = String(this.currentUserId).replace(/[{}"]/g, '').toLowerCase();
  return noteCreator === currentUser
  // console.log('canEditNote check:', {
  //   noteCreator: noteCreator,
  //   currentUserId: this.currentUserId,
  //   canEdit: canEdit,
  //   noteId: note.NoteId
  // });
  // return canEdit;
}

  loadNotes(): void {
    if (!this.TransactionType || !this.TransactionId) return;

    this.isLoading = true;

    const params: NoteFilterParams = {
      TransactionType: this.TransactionType,
      TransactionId: this.TransactionId,
      PageNumber: this.pageNumber,
      PageSize: this.pageSize,
      SearchTerm: this.searchTerm || null,
      NoteType: this.filterNoteType || null,
      IsPinned: this.activeFilterTab === 'pinned' ? true : null,  
      IsArchived: this.activeFilterTab === 'archived' ? true : null,
      HasReminder: this.activeFilterTab === 'reminder' ? true : null,
    };

    this.notesService.getNotesByTransaction(params).subscribe(
      (response) => {
        this.isLoading = false;

        console.log('Notes API Full Response:', response);

        let notesData = [];

        if (response) {
          if (response.notes) {
            notesData = response.notes;
          }
          else if (response.data) {
            notesData = response.data;
          }
          else if (response.Data) {
            notesData = response.Data;
          }
          else if (response.Table) {
            notesData = response.Table;
          }
          else if (Array.isArray(response)) {
            notesData = response;
          }
        }

        this.notes = Array.isArray(notesData) ? notesData : [];

        this.hasDueReminders = this.notes.some((x: any) => {
          return x.HasReminder //&& x.ReminderDate && new Date(x.ReminderDate) <= new Date();
        });

        this.totalCount =
          this.notes.length > 0
            ? (this.notes[0].TotalCount || this.notes.length)
            : 0;

        console.log('Final Notes:', this.notes);

        this.noteCountChange.emit(this.totalCount);
        // this.isLoading = false;

        // if (response && response.notes) {
        //   this.notes = response.notes;
        //   this.totalCount = response.totalCount;
        // } else if (response && Array.isArray(response)){
        //   this.notes = response;
        //   this.totalCount = response.length > 0 ? response[0].TotalCount || response.length : 0;
        // } else {
        //   this.notes = [];
        //   this.totalCount = 0;
        // }
        // this.noteCountChange.emit(this.totalCount)
      },
      () => {
        this.isLoading = false;
        this.toastr.error('Failed to load notes.', 'Error');
        console.error('Load notes error:', Error);
      }
    );
  }

  loadTrashedNotes(): void {
    this.isTrashLoading = true;
    this.notesService.getTrashedNotes(this.trashPageNumber, this.pageSize).subscribe(
      (response) => {
        this.isTrashLoading = false;

        if (response && response.notes) {
          this.trashedNotes = response.notes;
          this.trashTotalCount = response.totalCount;
        }
        else if (response && Array.isArray(response)) {
          this.trashedNotes = response;
          this.trashTotalCount = response.length > 0 ? (response[0].TotalCount || response.length) : 0;
        }
        else {
          this.trashedNotes = [];
          this.trashTotalCount = 0;
        }

        console.log('Trashed notes count:', this.trashedNotes.length);
        console.log('Trash total count:', this.trashTotalCount);
      },
      (error) => {
        this.isTrashLoading = false;
        console.error('Error loading trashed notes:', error);
        this.toastr.error('Failed to load trash.', 'Error');
      }
    );
  }


  openCreateEditor(): void {
    this.selectedNote = null;
    this.currentView = 'editor';
  }

  openEditEditor(note: NoteViewModel): void {
    this.selectedNote = note;
    this.currentView = 'editor';
  }

  openTrash(): void {
    this.currentView = 'trash';
    this.loadTrashedNotes();
  }

  backToList(): void {
    this.currentView = 'list';
    this.selectedNote = null;
    this.loadNotes();
  }


  setFilterTab(tab: FilterTab): void {
    this.activeFilterTab = tab;
    this.pageNumber = 1;
    this.loadNotes();
  }

  onSearchChange(): void {
    this.searchSubject.next(this.searchTerm);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.pageNumber = 1;
  }

  onNoteTypeFilterChange(): void {
    this.pageNumber = 1;
    this.loadNotes();
  }


  onNoteSaved(note: NoteViewModel): void {
    this.backToList();
    this.refreshParentList.emit();
  }

  onEditorCancel(): void {
    this.backToList();
  }


  onPinToggle(noteId: string): void {
    this.notesService.togglePin(noteId).subscribe(
      () => {
        this.loadNotes();
        this.toastr.success('Pin status updated.', 'Success');
      },
      () => this.toastr.error('Failed to update pin.', 'Error')
    );
  }

  onArchiveToggle(noteId: string): void {
    this.notesService.toggleArchive(noteId).subscribe(
      () => {
        this.loadNotes();
        this.toastr.success('Archive status updated.', 'Success');
      },
      () => this.toastr.error('Failed to update archive.', 'Error')
    );
  }

  confirmDelete(noteId: string): void {
    this.showDeleteConfirm = noteId;
  }

  cancelDelete(): void {
    this.showDeleteConfirm = null;
  }

  onSoftDelete(noteId: string): void {
    this.notesService.softDeleteNote(noteId).subscribe(
      () => {
        this.showDeleteConfirm = null;
        this.loadNotes();
        this.refreshParentList.emit();
        this.toastr.success('Note moved to trash.', 'Success');
      },
      () => {
        this.showDeleteConfirm = null;
        this.toastr.error('Failed to delete note.', 'Error');
      }
    );
  }

  confirmPermanentDelete(noteId: string): void {
    this.showPermDeleteConfirm = noteId;
    this.refreshParentList.emit();
  }

  cancelPermanentDelete(): void {
    this.showPermDeleteConfirm = null;
  }

  onPermanentDelete(noteId: string): void {
    this.notesService.permanentDeleteNote(noteId).subscribe(
      () => {
        this.showPermDeleteConfirm = null;
        this.loadTrashedNotes();
        this.toastr.success('Note permanently deleted.', 'Success');
      },
      () => {
        this.showPermDeleteConfirm = null;
        this.toastr.error('Failed to delete note.', 'Error');
      }
    );
  }

  onRestore(noteId: string): void {
    this.notesService.restoreNote(noteId).subscribe(
      () => {
        this.loadTrashedNotes();
        this.refreshParentList.emit();
        this.toastr.success('Note restored.', 'Success');
      },
      () => this.toastr.error('Failed to restore note.', 'Error')
    );
  }


  get totalPages(): number {
    return Math.ceil(this.totalCount / this.pageSize);
  }

  get hasNextPage(): boolean {
    return this.pageNumber < this.totalPages;
  }

  get hasPrevPage(): boolean {
    return this.pageNumber > 1;
  }

  goToPage(page: number): void {
    this.pageNumber = page;
    this.loadNotes();
  }


  closePanel(): void {
    this.onClose.emit();
  }

  resetState(): void {
    this.currentView = 'list';
    this.pageNumber = 1;
    this.searchTerm = '';
    this.activeFilterTab = 'all';
    this.filterNoteType = '';
    this.notes = [];
    this.totalCount = 0;
    this.selectedNote = null;
  }

  get pinnedNotes(): NoteViewModel[] {
    if (!this.notes || !Array.isArray(this.notes)) {
      return [];
    }
    return this.notes.filter((n) => n && n.IsPinned === true);
  }

  get unpinnedNotes(): NoteViewModel[] {   
    if (!this.notes || !Array.isArray(this.notes)) {
      return [];
    }
    return this.notes.filter((n) => n && !n.IsPinned);
  }

  trackByNoteId(_: number, note: NoteViewModel): string {   
    return note && note.NoteId ? note.NoteId : _.toString();
  }
}
